import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  Auth,
  UserCredential,
  User,
  onAuthStateChanged,
} from 'firebase/auth';

import { loginAction, logoutAction } from '../../redux/slice/auth/auth.slice';
import { LoginDTO, SignupDTO } from '../../redux/slice/auth/auth.types';
import store from '../../redux/store';
import logger from '../logger';

/**
 * the currently authenticated user
 */
let currentUser: User | null;

/**
 * registers a new user's account
 *
 * @todo save user's first and last name
 */
export async function signup({ email, password }: SignupDTO): Promise<User> {
  const auth: Auth = getAuth();

  try {
    const result: UserCredential = await createUserWithEmailAndPassword(auth, email, password);
    logger.debug('Successfully signed up.', result);
    setCurrentUser(result.user);
    return result.user;
  } catch (e: unknown) {
    logger.error('Failed to signup.', e);
    throw e;
  }
}

/**
 * login to a user's account
 */
export async function login({ email, password }: LoginDTO): Promise<User> {
  const auth: Auth = getAuth();

  try {
    const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);
    logger.debug('Successfully logged in.', result);
    setCurrentUser(result.user);
    return result.user;
  } catch (e: unknown) {
    logger.error('Failed to login.', e);
    throw e;
  }
}

/**
 * log out of the currently authenticated user
 */
export async function logout(): Promise<void> {
  const auth: Auth = getAuth();

  try {
    await signOut(auth);
    setCurrentUser(null);
    logger.debug('Successfully logged out.');
  } catch (e: unknown) {
    logger.error('Failed to logout.', e);
    throw e;
  }
}

/**
 * return the currently authenticated user
 */
export function getCurrentUser(): User | null {
  if (currentUser) {
    return currentUser;
  }

  const auth: Auth = getAuth();
  return auth.currentUser;
}

/**
 * listens to changes to authentication state
 */
export function registerAuthObserver(): void {
  const auth: Auth = getAuth();

  onAuthStateChanged(auth, (user: User | null) => {
    if (user) {
      logger.debug('Auth state change: user logged in.', user);
      store.dispatch(loginAction(user));
    } else {
      logger.debug('Auth state change: user not authenticated.', user);
      store.dispatch(logoutAction());
    }
  });
}

/**
 * sets the currently authenticated user
 */
export function setCurrentUser(user: User | null): void {
  currentUser = user;
}
